import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { useAuth } from '../auth/AuthContext';
import { useColorMode } from '../ThemeContext';
import { tenantFetch, formatCents, TenantInvoice, InvoiceStatus } from './api';
import { generateInvoicePdf } from '../utils/generateInvoicePdf';

type ChipColor = 'warning' | 'success' | 'error' | 'default';

const STATUS_MAP: Record<InvoiceStatus, { label: string; color: ChipColor; icon: React.ReactElement }> = {
  pending: { label: 'Due', color: 'warning', icon: <HourglassEmptyOutlinedIcon sx={{ fontSize: '12px !important' }} /> },
  paid: { label: 'Paid', color: 'success', icon: <CheckCircleOutlineIcon sx={{ fontSize: '12px !important' }} /> },
  failed: { label: 'Failed', color: 'error', icon: <ErrorOutlineIcon sx={{ fontSize: '12px !important' }} /> },
  returned: { label: 'Returned', color: 'error', icon: <ErrorOutlineIcon sx={{ fontSize: '12px !important' }} /> },
  void: { label: 'Void', color: 'default', icon: <RemoveCircleOutlineIcon sx={{ fontSize: '12px !important' }} /> },
  draft: { label: 'Draft', color: 'default', icon: <RemoveCircleOutlineIcon sx={{ fontSize: '12px !important' }} /> },
};

function InvoiceRow({
  invoice,
  navigate,
}: {
  invoice: TenantInvoice;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const totalCents = invoice.totalAmountCents ?? invoice.amount;
  const { label, color, icon: statusIcon } = STATUS_MAP[invoice.status] ?? {
    label: invoice.status,
    color: 'default' as ChipColor,
    icon: <RemoveCircleOutlineIcon sx={{ fontSize: '12px !important' }} />,
  };
  const tenantGal = invoice.metadata?.tenantGallons;
  const prevCents = invoice.previousCycleAmountCents;
  const changePct =
    prevCents !== undefined && prevCents > 0
      ? ((totalCents - prevCents) / prevCents) * 100
      : undefined;

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    generateInvoicePdf({
      propertyName: invoice.propertyName,
      propertyAddress: invoice.propertyAddress,
      tenantName: invoice.tenantName,
      unitNumber: invoice.unitNumber,
      period: invoice.period,
      totalAmountCents: totalCents,
      lineItems: invoice.lineItems ?? [{ label: 'Water', amountCents: totalCents }],
    });
  };

  return (
    <Box
      onClick={() => navigate(`/tenant/invoices/${invoice.id}`)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2.5,
        py: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: invoice.status === 'pending' ? 'warning.light' : 'divider',
        bgcolor: 'background.paper',
        cursor: 'pointer',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: '0 2px 8px rgba(37,99,235,0.1)',
        },
      }}
    >
      {/* Amount + period */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: 'text.primary' }}
          >
            {formatCents(totalCents)}
          </Typography>
          <Chip
            icon={statusIcon}
            label={label}
            color={color}
            size="small"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
          {changePct !== undefined && (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                color: changePct >= 0 ? 'warning.main' : 'success.main',
              }}
            >
              {changePct >= 0 ? '+' : ''}{changePct.toFixed(1)}%
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {invoice.period ?? invoice.description ?? `Invoice ${invoice.id.slice(0, 8)}`}
          </Typography>
          {tenantGal !== undefined && (
            <>
              <Typography variant="caption" color="text.disabled">·</Typography>
              <Stack direction="row" spacing={0.4} alignItems="center">
                <WaterDropOutlinedIcon sx={{ fontSize: 11, color: 'primary.main' }} />
                <Typography variant="caption" color="text.secondary">
                  {tenantGal.toLocaleString()} gal
                </Typography>
              </Stack>
            </>
          )}
          {invoice.unitNumber && (
            <>
              <Typography variant="caption" color="text.disabled">·</Typography>
              <Typography variant="caption" color="text.secondary">
                Unit {invoice.unitNumber}
              </Typography>
            </>
          )}
        </Stack>
      </Box>

      {/* Actions */}
      <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
        <Tooltip title="Download invoice PDF">
          <IconButton size="small" onClick={handleDownload} aria-label="Download invoice PDF" sx={{ color: 'text.secondary' }}>
            <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        {invoice.status === 'pending' && (
          <Button
            size="small"
            variant="contained"
            onClick={(e) => { e.stopPropagation(); navigate(`/tenant/invoices/${invoice.id}`); }}
            sx={{ ml: 0.5 }}
          >
            Pay now
          </Button>
        )}
        {(invoice.status === 'failed' || invoice.status === 'returned') && (
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={(e) => { e.stopPropagation(); navigate('/tenant/setup'); }}
            sx={{ ml: 0.5 }}
          >
            Resolve
          </Button>
        )}
      </Stack>
    </Box>
  );
}

export default function TenantInvoiceList() {
  const { user, signOut, getIdToken } = useAuth();
  const navigate = useNavigate();
  const { mode, toggleMode } = useColorMode();
  const [invoices, setInvoices] = useState<TenantInvoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const token = await getIdToken(false);
      if (!token || cancelled) return;

      const [meRes, invRes] = await Promise.all([
        tenantFetch('/tenants/me', token),
        tenantFetch('/tenants/me/invoices', token),
      ]);
      if (cancelled) return;

      if (!meRes.ok) {
        setError('Failed to load account info. Please try again.');
        return;
      }
      const meData = await meRes.json();
      if (!meData.paymentMethodAttached) {
        navigate('/tenant/setup', { replace: true });
        return;
      }

      if (!invRes.ok) {
        setError('Failed to load invoices. Please try again.');
        return;
      }
      const invData = await invRes.json();
      if (!cancelled) setInvoices(invData.invoices ?? []);
    })();
    return () => { cancelled = true; };
  }, [user, getIdToken, navigate]);

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography
            variant="h6"
            sx={{ flexGrow: 1, fontFamily: "'Fraunces', serif", fontWeight: 600, letterSpacing: '-0.5px' }}
          >
            Utilibill
          </Typography>
          {user?.email && (
            <Typography variant="body2" sx={{ mr: 2, opacity: 0.7, fontSize: '0.8rem' }}>
              {user.email}
            </Typography>
          )}
          <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton
              color="inherit"
              onClick={toggleMode}
              aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              sx={{ opacity: 0.8, mr: 0.5, '&:hover': { opacity: 1 } }}
            >
              {mode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </IconButton>
          </Tooltip>
          <Button
            color="inherit"
            onClick={() => void signOut()}
            sx={{ opacity: 0.8, fontSize: '0.8rem', '&:hover': { opacity: 1 } }}
          >
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ mt: 4, pb: 8 }}>
        <Typography
          variant="h4"
          sx={{ fontFamily: "'Fraunces', serif", fontWeight: 700, color: 'text.primary', mb: 1 }}
        >
          Your invoices
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Your water bill share for each billing period.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {invoices === null && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {invoices !== null && invoices.length === 0 && (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No invoices yet. You'll see them here once your property manager generates the first bill.
            </Typography>
          </Box>
        )}

        {invoices !== null && invoices.length > 0 && (
          <Stack spacing={1.5}>
            {invoices.map((inv) => (
              <InvoiceRow key={inv.id} invoice={inv} navigate={navigate} />
            ))}
          </Stack>
        )}
      </Container>
    </>
  );
}
